import { type RefObject, useEffect } from 'react';

/**
 * Where an anchored popup (the Format menu, the drill menu) opens: the box it
 * sits beside, read at the moment it is positioned — a function, so a scroll
 * or resize between open and paint does not strand it.
 */
export type PopupAnchor = () => DOMRect | null;

/**
 * Put the menu BESIDE its anchor: to the right when it fits, otherwise to the
 * left, top-aligned and clamped into the viewport.
 *
 * Beside, not below, because below a column header is the column — and the
 * column is where the reader watches each choice land.
 *
 * PLACED FROM THE ANCHOR ONLY ON OPEN, AND WHEN THE PAGE MOVES (a scroll
 * outside the menu, a window resize). Picking a value must not move it: the
 * anchor itself can shift as a result — the shelf's badges appear beside its
 * Format button — and a menu that re-read the anchor on every change of its
 * own size jumped after one pick and was left behind after the next. When the
 * menu's own height changes it keeps its top, and moves only as far as it
 * must to stay on screen.
 */
export function Anchored({
  panelRef,
  anchor,
}: {
  panelRef: RefObject<HTMLDivElement | null>;
  anchor: PopupAnchor;
}) {
  useEffect(() => {
    const fromAnchor = () => {
      const panel = panelRef.current;
      const at = anchor();
      if (!panel || !at) return;
      const { offsetWidth: w, offsetHeight: h } = panel;
      const gap = 6;
      const right = at.right + gap;
      const left =
        right + w <= window.innerWidth - 8
          ? right
          : at.left - gap - w >= 8
            ? at.left - gap - w
            : // Neither side has room (a very narrow window): overlap, clamped.
              Math.max(8, window.innerWidth - w - 8);
      panel.style.left = `${left}px`;
      panel.style.top = `${Math.max(8, Math.min(at.top, window.innerHeight - h - 8))}px`;
    };
    const keepOnScreen = () => {
      const panel = panelRef.current;
      if (!panel) return;
      const top = Number.parseFloat(panel.style.top) || 0;
      const fitted = Math.max(8, Math.min(top, window.innerHeight - panel.offsetHeight - 8));
      if (fitted !== top) panel.style.top = `${fitted}px`;
    };
    const onScroll = (event: Event) => {
      // The menu's own overflow scrolling is not the page moving.
      if (event.target instanceof Node && panelRef.current?.contains(event.target)) return;
      fromAnchor();
    };
    fromAnchor();
    window.addEventListener('resize', fromAnchor);
    window.addEventListener('scroll', onScroll, true);
    const observer = new ResizeObserver(keepOnScreen);
    if (panelRef.current) observer.observe(panelRef.current);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', fromAnchor);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [panelRef, anchor]);
  return null;
}
